import { Star } from 'lucide-react'
import { useLocale } from '../i18n/LocaleContext'
import { useTestimonials } from '../hooks/useTestimonials'
import SectionHeading from './ui/SectionHeading'
import type { Testimonial } from '../types/wp'

// Fallback testimonials shown when WordPress is not connected
const fallbackTestimonials: Testimonial[] = [
  {
    id: 1,
    parentName: 'Kovács Anna',
    childAge: '8',
    campYear: 2025,
    rating: 5,
    parentPhoto: '',
    quote: {
      hu: 'A gyermekem imádta a tábort! Hazafelé az úton már alig várta a következő évet. A szervezés kifogástalan volt, és a programok változatosak és érdekesek.',
      ro: 'Copilul meu a adorat tabăra! Pe drumul spre casă abia aștepta anul următor. Organizarea a fost impecabilă, iar programele variate și interesante.',
    },
  },
  {
    id: 2,
    parentName: 'Szabó Péter',
    childAge: '11',
    campYear: 2025,
    rating: 5,
    parentPhoto: '',
    quote: {
      hu: 'Végre egy tábor, ahol a gyerekek nem a telefont nyomkodják! A természetben töltött idő és a csapatépítő programok csodát tettek a fiammal.',
      ro: 'În sfârșit o tabără unde copiii nu stau pe telefon! Timpul petrecut în natură și programele de team building au făcut minuni cu fiul meu.',
    },
  },
  {
    id: 3,
    parentName: 'Pop Maria',
    childAge: '9',
    campYear: 2024,
    rating: 5,
    parentPhoto: '',
    quote: {
      hu: 'Biztonságos környezet, profi csapat és felejthetetlen élmények. Mindenkinek ajánlom a Magic Harghita Adventures táborokat!',
      ro: 'Mediu sigur, echipă profesionistă și experiențe de neuitat. Recomand tuturor taberele Magic Harghita Adventures!',
    },
  },
]

export default function Testimonials() {
  const { locale, t } = useLocale()
  const { testimonials: apiTestimonials, loading } = useTestimonials()

  const testimonials = apiTestimonials.length > 0 ? apiTestimonials : fallbackTestimonials

  if (loading) return null

  return (
    <section className="section-padding bg-surface">
      <div className="container-max">
        <SectionHeading title={t.testimonials.title} subtitle={t.testimonials.subtitle} />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((item) => (
            <div
              key={item.id}
              className="bg-white rounded-card p-6 shadow-subtle hover:shadow-hover transition-shadow"
            >
              {/* Stars */}
              <div className="flex gap-0.5 mb-4">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    size={16}
                    className={i < item.rating ? 'text-warning fill-warning' : 'text-gray-200'}
                  />
                ))}
              </div>

              {/* Quote */}
              <blockquote className="text-text-secondary text-sm leading-relaxed mb-6">
                &ldquo;{item.quote[locale]}&rdquo;
              </blockquote>

              {/* Author */}
              <div className="flex items-center gap-3">
                {item.parentPhoto ? (
                  <img
                    src={item.parentPhoto}
                    alt={item.parentName}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                ) : (
                  <div
                    role="img"
                    aria-label={item.parentName}
                    className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center"
                  >
                    <span aria-hidden="true" className="text-primary font-medium text-sm">
                      {item.parentName.split(' ').map((n) => n[0]).join('')}
                    </span>
                  </div>
                )}
                <div>
                  <p className="text-sm font-medium text-secondary">{item.parentName}</p>
                  <p className="text-xs text-text-muted">
                    {t.testimonials.childAge}: {item.childAge} | {item.campYear}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
